import { useSelector } from 'react-redux';
import {
  FREESTAR_ENABLED,
  FREESTAR_PRIMIS_VIDEO_ENABLED,
  OUTSTREAM_VIDEO_AD_ENABLED,
} from 'src/common/constants/experiments/ads';
import useExperiment from 'src/common/hooks/useExperiment';
import { useStores } from 'src/common/providers';
import { selectRouteGuideId } from 'src/common/selectors/app';
import { selectIsNowPlayingDialogOpen } from 'src/common/selectors/dialog';
import { useDetermineAdEligibility } from 'src/common/utils/ads/determineAdEligibility';
import determineSponsorshipEnabled from 'src/common/utils/ads/determineSponsorshipEnabled';
import FreeStarVideoAdContainer from './FreeStarVideoAdContainer';
import { OutstreamVideoAd } from './OutstreamVideoAd';

interface OustreamVideoAdSelectorProps {
  id: string;
  isInNowPlayingDialog?: boolean;
  center?: boolean;
}

export function OutstreamVideoAdSelector(props: OustreamVideoAdSelectorProps) {
  const { id, isInNowPlayingDialog = false, center = false } = props;
  const { playerStore } = useStores();
  const { canShowNowPlayingMrecVideoAds = true } = playerStore.metadata()?.ads ?? {};
  const isNowPlayingDialogOpen = useSelector(selectIsNowPlayingDialogOpen);
  const guideId = useSelector(selectRouteGuideId);
  const sponsorshipEnabled = useSelector(determineSponsorshipEnabled(guideId));
  const canShowAds = useDetermineAdEligibility(guideId);
  const isFreestarEnabled = useExperiment(FREESTAR_ENABLED);
  const isFreestarPrimisVideoEnabled = useExperiment(FREESTAR_PRIMIS_VIDEO_ENABLED);
  const isOutstreamVideoAdEnabled = useExperiment(OUTSTREAM_VIDEO_AD_ENABLED);

  // Prevents outstream video from showing on-page and in NP dialog at the same time
  if (isNowPlayingDialogOpen !== isInNowPlayingDialog) {
    return null;
  }

  if (!canShowAds || sponsorshipEnabled || !canShowNowPlayingMrecVideoAds) {
    return null;
  }

  if (isFreestarEnabled && isFreestarPrimisVideoEnabled && !isOutstreamVideoAdEnabled) {
    return <FreeStarVideoAdContainer center={center} />;
  }

  if (isOutstreamVideoAdEnabled) {
    return <OutstreamVideoAd id={id} center={center} />;
  }

  return null;
}
